import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';

import { schedulingPatchScheduledAppSettings } from '@crossnokaye/typescript-sdk/funcs/schedulingPatchScheduledAppSettings';
import { settingsUpdateSettings } from '@crossnokaye/typescript-sdk/funcs/settingsUpdateSettings';
import {
  ControlledDeviceSetting,
  ForcePointTotals,
} from '@crossnokaye/typescript-sdk/models/components';
import {
  DesiredSettingValue,
  ScheduledInputValuePatch,
} from '@crossnokaye/typescript-sdk/models/components';
import {
  PatchScheduledAppSettingsRequest,
  PatchScheduledAppSettingsResponse,
  UpdateSettingsRequest,
  UpdateSettingsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';
import { useToaster } from '@crossnokaye/ux-common';

import { useAtlasClient } from '@/hooks/use-atlas-client';
import { useAuth } from '@/hooks/use-auth';
import {
  getForcePointTotalsQuery,
  GetForcePointTotalsQueryProps,
} from '@/queries/settings/get-force-point-totals-query';
import { listSettingsQuery, ListSettingsQueryProps } from '@/queries/settings/list-settings-query';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';
import { getApiErrorMessage } from '@/utils/error-utils';
import { withViewOnlyHint } from '@/utils/view-only-error-message';

export const useForcePointTotals = ({
  orgId,
  agentId,
  transform,
}: Omit<GetForcePointTotalsQueryProps, 'client'>): UseQueryResult<ForcePointTotals | undefined> => {
  const client = useAtlasClient();
  return useQuery(getForcePointTotalsQuery({ orgId, agentId, transform, client }));
};

export const useListSettings = ({
  orgId,
  agentId,
  deploymentId,
  aliases,
  deviceIds,
  deviceKinds,
  settingIds,
  transform,
}: Omit<ListSettingsQueryProps, 'client'>): UseQueryResult<
  Array<ControlledDeviceSetting> | undefined
> => {
  const client = useAtlasClient();
  return useQuery(
    listSettingsQuery({
      orgId,
      agentId,
      deploymentId,
      aliases,
      deviceIds,
      deviceKinds,
      settingIds,
      transform,
      client,
    })
  );
};

export interface PatchScheduledAppSettingsQueryProps extends AtlasQueryWithOrgIdAndAgentId {
  appId: string | undefined;
}

/**
 * Update the settings of a scheduled app.
 *
 * @param organizationId The organization ID
 * @param agentId The agent ID
 * @param appId The app ID
 * @param settings The settings to update
 */
export const usePatchScheduledAppSettings = ({
  orgId,
  agentId,
  appId,
}: PatchScheduledAppSettingsQueryProps): UseMutationResult<
  PatchScheduledAppSettingsResponse,
  Error,
  Array<ScheduledInputValuePatch>
> => {
  const { addErrorToast, addSuccessToast } = useToaster();
  const { isViewOnly } = useAuth();
  const client = useAtlasClient();

  return useMutation({
    mutationFn: (settings: Array<ScheduledInputValuePatch>) => {
      if (!orgId || !agentId || !appId) {
        throw new Error('Organization ID, Agent ID, and App ID are required');
      }
      return client<PatchScheduledAppSettingsRequest, PatchScheduledAppSettingsResponse>({
        atlasFunc: schedulingPatchScheduledAppSettings,
        request: {
          patchScheduledAppSettingsRequestBody: { settings: settings },
          orgId,
          agentId,
          appId,
        },
      });
    },
    onSuccess: () => {
      addSuccessToast('App settings updated');
    },
    onError: () => {
      addErrorToast(withViewOnlyHint('Failed to update app settings', isViewOnly));
    },
  });
};

/**
 * Update Settings
 *
 * @param organizationId The organization ID
 * @param agentId The agent ID
 * @param settings The settings to update
 */
export const useUpdateSettings = ({
  orgId,
  agentId,
  disableDefaultCallbacks = false,
  onMutate,
  onSuccess,
  onError,
}: AtlasQueryWithOrgIdAndAgentId & {
  disableDefaultCallbacks?: boolean;
  onMutate?: (settings: DesiredSettingValue[]) => void;
  onSuccess?: (response: UpdateSettingsResponse, settings: DesiredSettingValue[]) => void;
  onError?: (error: Error, settings: DesiredSettingValue[]) => void;
}): UseMutationResult<UpdateSettingsResponse, Error, DesiredSettingValue[]> => {
  const { addErrorToast, addSuccessToast } = useToaster();
  const client = useAtlasClient();

  return useMutation({
    mutationKey: ['updateSettings'],
    mutationFn: (settings: DesiredSettingValue[]) => {
      if (!orgId || !agentId) {
        throw new Error('Organization ID and Agent ID are required');
      }
      return client<UpdateSettingsRequest, UpdateSettingsResponse>({
        atlasFunc: settingsUpdateSettings,
        request: {
          orgId,
          agentId,
          updateSettingsRequestBody: { settings },
        },
      });
    },
    onSuccess: disableDefaultCallbacks
      ? (response, settings) => {
          onSuccess?.(response, settings);
        }
      : (response, settings) => {
          addSuccessToast('Action completed successfully!', 5000);
          onSuccess?.(response, settings);
        },
    onError: disableDefaultCallbacks
      ? (error: Error, settings) => {
          onError?.(error, settings);
        }
      : (error: Error, settings) => {
          const apiMessage = getApiErrorMessage(error);
          addErrorToast(`Failed to complete action! ${apiMessage}`, 5000);
          onError?.(error, settings);
        },
    onMutate: (settings) => {
      onMutate?.(settings);
    },
  });
};
